/**
 * AP-41 — Verträge des eigenen Mandanten: Stand, Liste, Annahme, Kenntnis, Widerspruch.
 *
 * Liegt in `lib/`, weil Menü-Layout (Hinweis, Sperrbildschirm) und die Einstellungsseite denselben
 * Stand brauchen. Ob der Betrieb gesperrt ist, entscheidet der Server; das Frontend zeigt es nur.
 */
import { create } from 'zustand'
import { api } from '@/lib/axios'
import { downloadFile } from '@/lib/download'

export type VertragsStand = 'ANGENOMMEN' | 'OFFEN' | 'UEBERFAELLIG' | 'MITTEILUNG' | 'WIDERSPROCHEN'
export type VertragsGeltung = 'ALL' | 'AI' | 'SELECTED'

export interface OffenerVertrag {
  documentId: string
  title: string
  scope: VertragsGeltung
  stand: VertragsStand
  frist: string | null
  pflicht: boolean
  versionId: string
  label: string
}

/** Nachtrag 1 — eine Person der Liste „Wer nimmt an?": Sie trägt die Rolle „Vertragsannahme". */
export interface Annehmender {
  userId: string
  name: string
  /**
   * Arbeitet die Person? Nur dann bindet ihr Eintrag — eine Eingeladene ist es bis zur Annahme
   * der Einladung nicht, eine Deaktivierte nicht mehr.
   */
  aktiv: boolean
  /** Unterscheidet „eingeladen" von „deaktiviert". */
  angemeldetGewesen: boolean
}

/** Wer eingetragen ist oder eingetragen werden kann (`GET /contracts/signers`). */
export interface AnnahmeKandidat extends Annehmender {
  email: string | null
  eingetragen: boolean
}

export interface VertragsStatus {
  companyName: string
  /** Gesperrt für **diesen Benutzer** — die Person des Betreibers ist es nie. */
  gesperrt: boolean
  /** Gesperrt für den Betrieb: Alle außer dem Betreiber warten auf die Annahme. */
  mandantGesperrt: boolean
  betreiberPerson: boolean
  kiGesperrt: boolean
  /** Wer eingetragen ist. Arbeitet keine dieser Personen (oder ist die Liste leer): jeder mit dem Recht. */
  annahmeDurch: Annehmender[]
  /** Darf dieser Benutzer annehmen — entschieden vom Server aus Recht und Liste. */
  darfAnnehmen: boolean
  offen: OffenerVertrag[]
}

export interface Zustimmung {
  id: string
  kind: 'ACCEPTED' | 'ACKNOWLEDGED' | 'OBJECTED'
  createdAt: string
  personName: string
  personEmail: string
  personRole: string
  reason: string | null
  contentHash: string
  document: { id: string; title: string }
  version: { id: string; number: number; label: string; fileName: string }
}

export interface VertragsDokument {
  documentId: string
  title: string
  scope: VertragsGeltung
  stand: VertragsStand
  frist: string | null
  pflicht: boolean
  version: {
    id: string
    number: number
    label: string
    mode: 'ACCEPT' | 'NOTICE'
    publishedAt: string | null
    changeSummary: string | null
    fileName: string
    contentHash: string
    sizeBytes: number
  }
}

export interface VertragsListe {
  dokumente: VertragsDokument[]
  verlauf: Zustimmung[]
}

export const vertraegeApi = {
  status: () => api.get<{ data: VertragsStatus }>('/contracts/status').then((r) => r.data.data),
  liste: () => api.get<{ data: VertragsListe }>('/contracts').then((r) => r.data.data),
  pdf: (versionId: string) => api.get<ArrayBuffer>(`/contracts/versions/${versionId}/pdf`, { responseType: 'arraybuffer' }).then((r) => r.data),
  annehmen: (versionId: string, daten: { role: string; password: string; confirmed: boolean }) =>
    api.post(`/contracts/versions/${versionId}/accept`, daten).then((r) => r.data.data),
  kenntnis: (versionId: string) => api.post(`/contracts/versions/${versionId}/acknowledge`).then((r) => r.data.data),
  widersprechen: (versionId: string, reason: string) => api.post(`/contracts/versions/${versionId}/object`, { reason }).then((r) => r.data.data),
  /** Nachtrag 1: „Wer nimmt an?" — lesen und als ganze Liste setzen. */
  annehmende: () => api.get<{ data: AnnahmeKandidat[] }>('/contracts/signers').then((r) => r.data.data),
  setzeAnnehmende: (userIds: string[]) => api.put<{ data: AnnahmeKandidat[] }>('/contracts/signers', { userIds }).then((r) => r.data.data),
}

/** Ereignis, das `axios.ts` bei `CONTRACT_ACCEPTANCE_REQUIRED` auslöst — ohne Import in die Gegenrichtung. */
export const VERTRAGSSPERRE_EREIGNIS = 'symphenia:vertragssperre'

interface VertragsZustand {
  status: VertragsStatus | null
  fuer: string | null
  laden: (fuer: string, neu?: boolean) => Promise<void>
}

/** Der Stand, einmal je Anmeldung — neu nach einer Annahme oder wenn der Server sperrt. */
export const useVertraege = create<VertragsZustand>((set, get) => ({
  status: null,
  fuer: null,
  laden: async (fuer, neu = false) => {
    if (!neu && get().fuer === fuer) return
    set({ fuer })
    try {
      const status = await vertraegeApi.status()
      if (get().fuer === fuer) set({ status })
    } catch {
      // Ohne Stand zeigen wir nichts an — die Sperre selbst sitzt ohnehin im Server.
    }
  },
}))

/**
 * PDF über das Backend holen (mit Rechteprüfung) und ausliefern wie jeden anderen PDF-Export —
 * Tab oder Download nach der Einstellung des Mandanten, mit Hinweis bei blockiertem Tab.
 */
export async function oeffnePdf(versionId: string, dateiname: string) {
  downloadFile(await vertraegeApi.pdf(versionId), dateiname, 'application/pdf')
}
